import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { ExternalLink, FlatButton } from '@framework/ui/atoms';
import { Loading } from '@framework/ui/atoms/Loading';
import { Modal } from '@framework/ui/elements';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

/**
 * Firebase Auth の linkWithRedirect でログイン中ユーザーに Microsoft アカウントを連携するモーダル。
 *
 * リダイレクト復帰後の検証(メールアドレス一致)と結果表示は MicrosoftLinkRedirectResultHandler が行う。
 */
export const ConnectMicrosoftModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const handleConnectWithMicrosoft = useCallback(async () => {
    setProcessing(true);

    try {
      await FirebaseAuthenticationClient.linkWithRedirectToMicrosoft();
    } catch (error) {
      console.error(error);
      toast.error(t('auth.microsoftLink.result.error'));
      setProcessing(false);
    }
  }, [t]);

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.microsoftConnect.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col items-center p-8">
        {processing ? (
          <Loading />
        ) : (
          <>
            <div className="pb-4">
              <Trans
                i18nKey="auth.microsoftConnect.modal.description"
                values={{ email: userEntity.mailAddress }}
                components={{ emailSpan: <span className="px-2" /> }}
              />
              <div className="pt-1 text-xs">
                <Trans
                  i18nKey="auth.microsoftConnect.modal.emailChangeNote"
                  components={{
                    contactFormLink: <ExternalLink href="https://balus-help.levii.co.jp/hc/ja/requests/new" />,
                  }}
                />
              </div>
            </div>

            <div className="flex w-80 flex-col space-y-4 px-5 py-2">
              <FlatButton color="brand" onClick={handleConnectWithMicrosoft}>
                <span className="pr-2 text-lg">
                  <FontAwesomeIcon icon={faMicrosoft} />
                </span>
                {t('auth.login.loginWithMicrosoft')}
              </FlatButton>
            </div>
          </>
        )}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
      </div>
    </Modal>
  );
};
